import { guidedActive } from '$lib/stores/guided';
import { resetHydration } from '$lib/stores/hydration';
import { setOriginalResults } from '$lib/stores/mixedResults';
import {
  hasSearched,
  queryResult,
  resultStats,
  scoreFilterRange,
  selectionScope
} from '$lib/stores/selection';
import type { CompanyWithContacts } from '$lib/types/api';

/**
 * Putting a set of companies on screen as *the* selection.
 *
 * # Why this is one function rather than six lines at each call site
 *
 * Because getting it wrong is silent, and there is now more than one caller.
 * A set that filled `queryResult` and forgot `mixedResults` shows nothing; one
 * that forgot `resetHydration` shows the previous set's rows under the new
 * set's names. Both are states this app has been in. The order matters, so
 * there is one place to get it right.
 *
 * It was written inside `runSearch`, with a comment saying exactly that. It
 * moved here when a saved list became the second thing that could produce a
 * selection.
 */

/** What the caller knows about the set beyond the rows themselves. */
export interface SeatedSelection {
  /**
   * How many matched in total, which is more than `companies` holds when the
   * server capped the answer. Defaults to the number of rows.
   */
  total?: number;

  /** Whether the answer was trimmed. */
  capped?: boolean;

  /**
   * Whether the filters could produce this set again on their own.
   *
   * False for everything that seats a selection today, which is why it is the
   * default: a saved list is a set assembled by hand, and a full-scope search
   * answered a phrase that lives in no filter. Neither can be described to
   * `/deep-iq/ids` by the filters, so re-filtering has to name its members —
   * see `selectionScope`. Getting this wrong in the true direction is what
   * made a loaded list impossible to refine: the filter panel ran an ordinary
   * search, and the search knew nothing about the list.
   */
  reproducible?: boolean;
}

export function seatSelection(
  companies: CompanyWithContacts[],
  { total, capped = false, reproducible = false }: SeatedSelection = {}
): void {
  // Clear what was hydrated before the rows change underneath it, then seat
  // the new rows in both stores, then say a search has happened.
  resetHydration();
  queryResult.set(companies);
  setOriginalResults(companies);

  resultStats.set({
    company_count: total ?? companies.length,
    contact_count: companies.reduce((n, c) => n + c.contacts.length, 0),
    capped
  });
  hasSearched.set(true);

  // The score filter belongs to the set it was narrowed against.
  //
  // `submitFilters` has always reset it for the same reason. The search box
  // did not, which was a latent way to run a search, get rows back and see an
  // empty screen — and a saved list would hit it every time, because its rows
  // carry no score at all and any floor above zero excludes all of them.
  scoreFilterRange.set([0, 100]);

  // What re-filtering this selection is allowed to draw from.
  //
  // The seated identifiers, not the rows on screen later: narrowing against
  // what is left makes filters one-way, so clearing a canton could not bring
  // back the companies it had excluded.
  selectionScope.set(reproducible ? null : companies.map((company) => company.id));

  // Leave the front door. The guide is what stands in front of an empty
  // selection, and there is one now — without this the results are behind the
  // screen that exists to produce them.
  guidedActive.set(false);
}
